import { Box } from '@material-ui/core';
import { Agent, getAgentById, patchSetAgentConfiguration } from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction } from 'react';

import { ConfigurationForm } from '@/components/modules/AgentDetail/AgentDetails/components/Configuration/components/ConfigurationForm';
import {
  AgentConfigurationFormSchema,
  AgentConfigurationFormType,
} from '@/components/modules/AgentDetail/AgentDetails/components/Configuration/schemas/AgentConfigurationFormSchema';
import { EditDialogWrapper } from '@/components/shared/components/EditDialogWrapper';
import { FieldVariant } from '@/components/shared/components/NewContextForms/types/FieldVariant';
import { useCache } from '@/hooks/requests/useCache';
import { useRequest } from '@/hooks/requests/useRequest';

export const ConfigurationEditDialog = ({
  opened,
  setOpened,
  toggleDialog,
  currentAgent,
}: {
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  toggleDialog;
  currentAgent: Agent;
}) => {
  const { request, loading } = useRequest();
  const { revalidate } = useCache(getAgentById, { agentId: currentAgent.id });

  const agentConfigurationFormDefaultValues = {
    name: currentAgent.name,
    customTheme: currentAgent.customTheme,
    customRegistry: currentAgent.customRegistry,
    customLanguage: currentAgent.customLanguage,
    communicationsAreAllowed: currentAgent.communicationsAreAllowed,
    isBankReaderEnabled: currentAgent.isBankReaderEnabled,
    quotaSelectionByBorrowerIsAllowed: currentAgent.quotaSelectionByBorrowerIsAllowed,
    isDocumentationRequiredManagementEnabled: currentAgent.isDocumentationRequiredManagementEnabled,
    areCertificationsEnabled: currentAgent.areCertificationsEnabled,
    maxBankReaderAttempts: currentAgent.maxBankReaderAttempts,
    allowedPurchaseCountries: currentAgent.allowedPurchaseCountries,
    allowedAssetTypes: currentAgent.allowedAssetTypes,
    allowedProducts: currentAgent.allowedProducts,
    allowedOnboardingFulfilledByTypes: currentAgent.allowedOnboardingFulfilledByTypes,
    allowedArticleTypes: currentAgent.allowedArticleTypes,
    allowedPaymentMethodTypes: currentAgent.allowedPaymentMethodTypes,
    isStatusAnalysingEnabled: currentAgent.isStatusAnalysingEnabled,
  };

  const onSubmitForm = async (useFormValues: AgentConfigurationFormType) => {
    const patchSetAgentConfigurationPayload = {
      id: currentAgent.id,
      name: useFormValues.name,
      configuration: {
        allowedPurchaseCountries: useFormValues.allowedPurchaseCountries,
        allowedAssetTypes: useFormValues.allowedAssetTypes,
        allowedOnboardingFulfilledByTypes: useFormValues.allowedOnboardingFulfilledByTypes,
        allowedArticleTypes: useFormValues.allowedArticleTypes,
        allowedPaymentMethodTypes: useFormValues.allowedPaymentMethodTypes,
        communicationsAreAllowed: useFormValues.communicationsAreAllowed,
        quotaSelectionByBorrowerIsAllowed: useFormValues.quotaSelectionByBorrowerIsAllowed,
        areCertificationsEnabled: useFormValues.areCertificationsEnabled,
        isBankReaderEnabled: useFormValues.isBankReaderEnabled,
        isDocumentationRequiredManagementEnabled: useFormValues.isDocumentationRequiredManagementEnabled,
        isStatusAnalysingEnabled: useFormValues.isStatusAnalysingEnabled,
        maxBankReaderAttempts: Number(useFormValues.maxBankReaderAttempts),
        customTheme: useFormValues.customTheme,
        customRegistry: useFormValues.customRegistry,
        customLanguage: useFormValues.customLanguage,
      },
    };
    await request(patchSetAgentConfiguration, patchSetAgentConfigurationPayload);
    toggleDialog();
    revalidate();
  };

  return (
    <EditDialogWrapper
      opened={opened}
      setOpened={setOpened}
      handleClose={toggleDialog}
      DialogProps={{
        maxWidth: 'sm',
        fullWidth: true,
      }}
      title={'AGENT_CONFIGURATION'}
      onSubmitForm={onSubmitForm}
      isLoading={loading}
      formSchema={AgentConfigurationFormSchema}
      defaultValues={agentConfigurationFormDefaultValues}
    >
      <Box pt={2}>
        <ConfigurationForm fullWidth fieldVariant={FieldVariant.form} currentAgent={currentAgent} />
      </Box>
    </EditDialogWrapper>
  );
};
